// ─── Build 2.07.0 — Operator Persona Service ─────────────────────────────────
// Stores extended operator identity: display name, avatar, timezone, bases,
// travel style, work devices, hours, goals.
//
// ARCHITECTURE: local-first, write-through to cloud via StorageManager.
// loadPersona()      — synchronous, localStorage only
// savePersona()      — localStorage + cloud push + data-changed event
// createPersona()    — factory for new operators (sets onboardingCompleted: true)
// updatePersona()    — partial update on existing persona
// resetOnboarding()  — clears onboardingCompleted ONLY; leaves all other data
// updateLastLogin()  — stamps the lastLogin field on every auth restore
// hydratePersonaFromCloud() — adopt cloud record if strictly newer

import type { OperatorPersona, LocationBase, TravelStyle, WorkDevice, RoamingStyle } from '@/types/operatorPersona';
import { ONBOARDING_VERSION } from '@/types/operatorPersona';
import { storageManager } from '@/services/storage/StorageManager';

export type { LocationBase, TravelStyle, WorkDevice, RoamingStyle };

const STORAGE_KEY = 'nomad:operator:persona:v1';

function now(): string {
  return new Date().toISOString();
}

function dispatchDataChanged() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('nomad:data-changed', { detail: { domain: 'persona' } }));
  }
}

// ─── Read ─────────────────────────────────────────────────────────────────────

/** Load the operator persona from localStorage. Returns null if not set up. */
export function loadPersona(): OperatorPersona | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as Partial<OperatorPersona>;
    // Normalise missing Phase-2 fields so callers don't need null-checks on every field.
    return {
      displayName:         p.displayName         ?? '',
      avatarDataUrl:       p.avatarDataUrl        ?? null,
      timezone:            p.timezone             ?? 'UTC',
      country:             p.country              ?? '',
      incomeGoalMonthly:   p.incomeGoalMonthly    ?? 0,
      dailyGoalUSD:        p.dailyGoalUSD         ?? Math.round((p.incomeGoalMonthly ?? 0) / 30),
      preferredCategories: p.preferredCategories  ?? [],
      homeBase:            p.homeBase             ?? null,
      currentBase:         p.currentBase          ?? null,
      travelStyle:         p.travelStyle          ?? null,
      workDevices:         p.workDevices          ?? [],
      hoursAvailable:      p.hoursAvailable       ?? 4,
      onboardingCompleted:     p.onboardingCompleted     ?? false,
      termsAcceptedVersion:    p.termsAcceptedVersion    ?? undefined,
      privacyAcceptedVersion:  p.privacyAcceptedVersion  ?? undefined,
      termsAcceptedAt:         p.termsAcceptedAt         ?? undefined,
      createdAt:           p.createdAt            ?? now(),
      updatedAt:           p.updatedAt            ?? now(),
      lastLogin:           p.lastLogin            ?? null,
      // Phase 4 (v3) fields — normalised so v1/v2 records need no migration.
      nickname:            p.nickname             ?? null,
      dreamBases:          Array.isArray(p.dreamBases) ? p.dreamBases : [],
      roamingStyle:        p.roamingStyle         ?? null,
      weeklyGoalUSD:       p.weeklyGoalUSD        ?? Math.round((p.dailyGoalUSD ?? 0) * 7),
      activationDate:      p.activationDate       ?? (p.onboardingCompleted ? (p.createdAt ?? null) : null),
      lastActivation:      p.lastActivation       ?? p.activationDate
                             ?? (p.onboardingCompleted ? (p.createdAt ?? null) : null),
      firstLoginComplete:  p.firstLoginComplete   ?? (p.onboardingCompleted ?? false),
      onboardingVersion:   p.onboardingVersion    ?? null,
      personaVersion:      p.personaVersion       ?? '1',
    };
  } catch {
    return null;
  }
}

// ─── Write ────────────────────────────────────────────────────────────────────

/** Save the operator persona to localStorage + cloud. Never throws. */
/**
 * savePersona — H-05 persona ownership gate.
 *
 * Refuses to downgrade the persona version. Once the operator has a v3 persona
 * (Activation Experience), no stale caller (plugin, old hydration path) can
 * overwrite it with an older-versioned record.
 *
 * Also preserves onboardingCompleted and activationDate: if the existing record
 * has these set, the incoming record may NOT clear them unless an explicit
 * `{ force: true }` override is passed.
 */
export function savePersona(persona: OperatorPersona, opts?: { force?: boolean }): void {
  try {
    // ── BOOT_PROBE: Q2 — persona write path ───────────────────────────────────
    console.log(
      `[BOOT_PROBE] savePersona called: onboardingCompleted=${persona.onboardingCompleted} ` +
      `force=${!!opts?.force} displayName=${persona.displayName ?? 'null'} ` +
      `currentBase=${persona.currentBase?.city ?? 'null'} ` +
      `homeBase=${persona.homeBase?.city ?? 'null'} ` +
      `caller=${new Error().stack?.split('\n')[2]?.trim() ?? 'unknown'}`,
    );
    if (!opts?.force) {
      const existing = loadPersona();
      if (existing) {
        // Version downgrade guard.
        const existingV = Number(existing.personaVersion ?? '1');
        const incomingV = Number(persona.personaVersion ?? '1');
        if (incomingV < existingV) {
          if (typeof console !== 'undefined') {
            console.warn(
              `[OperatorPersonaService] savePersona: refused version downgrade ` +
              `(incoming v${incomingV} < stored v${existingV}). Pass { force: true } to override.`,
            );
          }
          return;
        }

        // Immutable field protection: once set, onboardingCompleted and activationDate
        // may not be cleared by a caller that doesn't pass { force: true }.
        if (existing.onboardingCompleted && !persona.onboardingCompleted) {
          persona = { ...persona, onboardingCompleted: true };
        }
        if (existing.activationDate && !persona.activationDate) {
          persona = { ...persona, activationDate: existing.activationDate };
        }
      }
    }

    const record = { ...persona, updatedAt: now() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    dispatchDataChanged();
    // Strip large binary fields before cloud sync — avatarDataUrl is stored as
    // base64 and can exceed 1 MB, causing 413 PayloadTooLargeError on the API
    // persistence route. The avatar is preserved in localStorage; the cloud
    // record stores all other operator fields so it can be restored on any device.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { avatarDataUrl: _avatar, ...cloudRecord } = record;
    // ── BOOT_PROBE: Q2 — cloud push path ──────────────────────────────────────
    console.log(
      `[BOOT_PROBE] savePersona → storageManager.push('operator','persona') ` +
      `onboardingCompleted=${cloudRecord.onboardingCompleted} updatedAt=${cloudRecord.updatedAt}`,
    );
    storageManager.push('operator', 'persona', cloudRecord);
  } catch {
    // localStorage quota exceeded (large avatar base64) — best-effort
  }
}

// ─── Factories / mutations ────────────────────────────────────────────────────

/** Create a brand-new operator profile from onboarding data. */
export function createPersona(data: {
  displayName: string;
  avatarDataUrl: string | null;
  timezone: string;
  country: string;
  incomeGoalMonthly: number;
  dailyGoalUSD: number;
  preferredCategories: string[];
  homeBase?: LocationBase | null;
  currentBase?: LocationBase | null;
  travelStyle?: TravelStyle | null;
  workDevices?: WorkDevice[];
  hoursAvailable?: number;
  // Phase 4 — Activation Experience fields (all optional, additive)
  nickname?: string | null;
  dreamBases?: LocationBase[];
  roamingStyle?: RoamingStyle | null;
  weeklyGoalUSD?: number;
}): OperatorPersona {
  const ts = now();
  // ACTIVATION OCCURS EXACTLY ONCE: if a persona was activated before (e.g.
  // Replay Activation), preserve the original activationDate.
  const previous = loadPersona();
  const persona: OperatorPersona = {
    displayName:         data.displayName,
    avatarDataUrl:       data.avatarDataUrl,
    timezone:            data.timezone,
    country:             data.country,
    incomeGoalMonthly:   data.incomeGoalMonthly,
    dailyGoalUSD:        data.dailyGoalUSD,
    preferredCategories: data.preferredCategories,
    homeBase:            data.homeBase    ?? null,
    currentBase:         data.currentBase ?? null,
    travelStyle:         data.travelStyle ?? null,
    workDevices:         data.workDevices ?? [],
    hoursAvailable:      data.hoursAvailable ?? 4,
    onboardingCompleted: true,
    createdAt:           previous?.createdAt ?? ts,
    updatedAt:           ts,
    lastLogin:           ts,
    nickname:            data.nickname?.trim() ? data.nickname.trim() : null,
    dreamBases:          data.dreamBases ?? [],
    roamingStyle:        data.roamingStyle ?? null,
    weeklyGoalUSD:       data.weeklyGoalUSD ?? Math.round(data.dailyGoalUSD * 7),
    activationDate:      previous?.activationDate ?? ts,
    lastActivation:      ts,
    firstLoginComplete:  false,
    onboardingVersion:   ONBOARDING_VERSION,
    personaVersion:      '3',
  };
  // ── BOOT_PROBE: Q2 — createPersona always sets onboardingCompleted=true ──────
  console.log(
    `[BOOT_PROBE] createPersona: writing onboardingCompleted=true to persona ` +
    `displayName=${data.displayName} country=${data.country} ` +
    `currentBase=${data.currentBase?.city ?? 'null'} homeBase=${data.homeBase?.city ?? 'null'}`,
  );
  savePersona(persona);
  return persona;
}

/**
 * Mark that the operator has landed on Home after activation (First Home
 * Experience delivered). Idempotent — only writes on the first call.
 */
export function markFirstLoginComplete(): void {
  const existing = loadPersona();
  if (!existing || existing.firstLoginComplete) return;
  savePersona({ ...existing, firstLoginComplete: true });
}

/** Export the full persona as pretty-printed JSON (Developer Center). */
export function exportPersonaJSON(): string | null {
  const persona = loadPersona();
  return persona ? JSON.stringify(persona, null, 2) : null;
}

/**
 * Import a persona from JSON (Developer Center). Validates the minimum
 * shape (displayName string) and re-normalises through loadPersona().
 * Returns the imported persona or null when the JSON is invalid.
 */
export function importPersonaJSON(raw: string): OperatorPersona | null {
  try {
    const parsed = JSON.parse(raw) as Partial<OperatorPersona>;
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;
    if (typeof parsed.displayName !== 'string' || parsed.displayName.trim().length === 0) return null;
    // Structural validation: fields that other consumers iterate/index must be
    // the right shape (or absent — loadPersona() backfills defaults).
    const isLoc = (l: unknown) => typeof l === 'object' && l !== null
      && typeof (l as LocationBase).city === 'string'
      && typeof (l as LocationBase).locationId === 'string';
    if (parsed.dreamBases          !== undefined && !(Array.isArray(parsed.dreamBases) && parsed.dreamBases.every(isLoc))) return null;
    if (parsed.homeBase            != null && !isLoc(parsed.homeBase)) return null;
    if (parsed.currentBase         != null && !isLoc(parsed.currentBase)) return null;
    if (parsed.workDevices         !== undefined && !(Array.isArray(parsed.workDevices) && parsed.workDevices.every(d => d === 'desktop' || d === 'mobile'))) return null;
    if (parsed.preferredCategories !== undefined && !(Array.isArray(parsed.preferredCategories) && parsed.preferredCategories.every(c => typeof c === 'string'))) return null;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
    const normalised = loadPersona();
    if (normalised) savePersona(normalised); // re-normalised write-through to cloud
    return normalised;
  } catch {
    return null;
  }
}

/** Update specific fields on the existing persona. Returns null if no persona exists. */
export function updatePersona(updates: Partial<OperatorPersona>): OperatorPersona | null {
  const existing = loadPersona();
  if (!existing) return null;
  const updated: OperatorPersona = { ...existing, ...updates, updatedAt: now() };
  savePersona(updated);
  return updated;
}

/**
 * Stamp the lastLogin timestamp.
 * Called by Shell every time useGetMe resolves a valid session.
 */
export function updateLastLogin(): void {
  const existing = loadPersona();
  if (!existing) return;
  savePersona({ ...existing, lastLogin: now() });
}

/**
 * Reset onboarding state ONLY.
 * Leaves all other persona data (wallet, passport, journey, etc.) intact.
 * Used by Developer Center "Reset Onboarding" action.
 */
export function resetOnboarding(): void {
  const existing = loadPersona();
  if (!existing) {
    // If no persona exists, nothing to reset.
    return;
  }
  // force: true — resetOnboarding() intentionally clears the completion flag;
  // the ownership gate must not block this internal lifecycle operation.
  savePersona({ ...existing, onboardingCompleted: false }, { force: true });
}

/**
 * SUPABASE FOUNDATION™ boot hydration: adopt the cloud persona only if
 * strictly newer than the last local write (StorageManager enforces this).
 * Broadcasts through the existing nomad:data-changed channel.
 */
export async function hydratePersonaFromCloud(): Promise<boolean> {
  // ── BOOT_PROBE: Q3 — cloud load path on next login ────────────────────────
  console.log('[BOOT_PROBE] hydratePersonaFromCloud → calling storageManager.hydrate(operator, persona)');
  const localBefore = loadPersona();
  console.log(
    `[BOOT_PROBE] hydratePersonaFromCloud: localStorage BEFORE hydrate: ` +
    `exists=${localBefore !== null} ` +
    `onboardingCompleted=${localBefore?.onboardingCompleted ?? 'null'} ` +
    `updatedAt=${localBefore?.updatedAt ?? 'null'}`,
  );
  const cloud = await storageManager.hydrate<OperatorPersona>('operator', 'persona');
  if (!cloud || typeof cloud !== 'object') {
    console.log('[BOOT_PROBE] hydratePersonaFromCloud: cloud returned null/empty → NOT adopting. localStorage unchanged.');
    return false;
  }
  const p = cloud as OperatorPersona;
  console.log(
    `[BOOT_PROBE] hydratePersonaFromCloud: cloud record received: ` +
    `onboardingCompleted=${p.onboardingCompleted} ` +
    `displayName=${p.displayName ?? 'null'} ` +
    `currentBase=${p.currentBase?.city ?? 'null'} ` +
    `updatedAt=${p.updatedAt ?? 'null'}`,
  );
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cloud));
    console.log('[BOOT_PROBE] hydratePersonaFromCloud: localStorage updated with cloud record ✓');
  } catch {
    console.warn('[BOOT_PROBE] hydratePersonaFromCloud: localStorage.setItem FAILED');
    return false;
  }
  dispatchDataChanged();
  return true;
}

/**
 * Clear the persona from localStorage only — does NOT touch the cloud record.
 *
 * Use this on logout so the local device is wiped clean for the next user,
 * while the operator's cloud persona is preserved for restoration on the next
 * login (hydratePersonaFromCloud / waitForProfileHydration).
 *
 * WHY separate from clearPersona:
 *   clearPersona() calls storageManager.remove() which queues a cloud DELETE.
 *   That DELETE fires on the next login (queue flush with new auth token) and
 *   permanently deletes the operator persona from Supabase. Every subsequent
 *   login then finds nothing in localStorage OR cloud → the onboarding wizard
 *   re-runs on every single login. This is the #1 cause of "onboarding repeats".
 */
export function clearLocalPersona(): void {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}

/** Clear the persona completely — localStorage + cloud (used by Factory Reset). */
export function clearPersona(): void {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  storageManager.remove('operator', 'persona');
}

/**
 * Derive initials from a display name.
 * "Jane Bloggs" → "JB", "Operator" → "OP"
 */
export function getInitials(displayName: string): string {
  const parts = displayName.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return displayName.slice(0, 2).toUpperCase();
}
